import { routePage, TRoutePage } from './route-page';

// Enige bron voor "wie is mijn hiërarchische ouder". Vervangt de losse terugkeer-constanten die
// eerder per route-component gehardcodeerd stonden. De NavigationStackService gebruikt deze map
// twee kanten op: om de voorouderketen van een pagina te bepalen (afkappen bij een zijsprong) en om
// bij een deep link/F5 een stack te synthetiseren voor een pagina waar nog geen historie van is.
//
// De map volgt de URL-structuur. Een pagina die vanuit meer dan één plek bereikbaar is en dus geen
// eenduidige ouder heeft (zie "dubbel-context pagina's" verderop) krijgt hier bewust null: de stack
// vult ontbrekende voorouders aan, dus een verkeerd gekozen vaste ouder schuift een frame tussen dat
// de gebruiker nooit bezocht en veegt de echte aanroeper van de keten.
export const pageParent: Record<TRoutePage, TRoutePage | null> = {
    // Wortel en losstaande auth-pagina's
    [routePage.home]: null,
    [routePage.login]: null,
    [routePage.confirm]: null,
    [routePage.signup]: null,
    [routePage.recover]: routePage.login,
    [routePage.password]: null,
    [routePage.register]: routePage.login,
    [routePage.account]: routePage.home,
    // Vanuit het accountmenu in de header (overal bereikbaar); terug valt via de stack op de aanroeper,
    // Home is alleen de terugval bij een deep link/F5.
    [routePage.emailChange]: routePage.home,
    [routePage.passwordChange]: routePage.home,
    [routePage.settings]: routePage.home,
    [routePage.user]: null,

    // Dubbel-context pagina's: vanuit meer dan één plek te openen, waarbij de échte navigatie-url
    // niets zegt over de herkomst. Image kent dit patroon (foto's op elke entiteit, geen eigen
    // hiërarchie). Geen vaste ouder ⇒ regel 4 (aanroeper bewaren) geldt altijd: vanuit welke pagina
    // dan ook geopend blijft die pagina als aanroeper op de stack staan.
    [routePage.image]: null,
    // Chat: vanuit de chats-tab van een project én vanuit het centrale overzicht op Home.
    [routePage.chat]: null,
    // Bericht: vanuit de messages-tabs (Project, Domain, Function) én vanuit het centrale overzicht op Home.
    [routePage.message]: null,

    // AdminPanel
    [routePage.adminPanel]: routePage.home,
    [routePage.adminActivity]: routePage.adminPanel,
    [routePage.adminClaim]: routePage.adminPanel,
    [routePage.adminClaimAction]: routePage.adminPanel,
    [routePage.adminCreditType]: routePage.adminPanel,
    // adminDomainUser (/admin-panel/domainUsers/{key}) en domainUser (/domains/{dauGuid}/domainUsers/
    // {key}): zelfde DomainUserComponent, twee routes — zie DomainUserComponent.pageCurrent_forStack.
    // De leden van domain 0 (de beheerders) horen onder AdminPanel, niet onder een Domain "Reflecs".
    [routePage.adminDomainUser]: routePage.adminPanel,
    // adminFunction (/admin-panel/functions/{key}, geen dauGuid) en function (/domains/{dauGuid}/
    // functions/{key}) zijn twee echt verschillende routes voor dezelfde FunctionComponent.
    // FunctionComponent.pageCurrent_name blijft statisch routePage.function (elders relevant), maar
    // FunctionComponent.pageCurrent_forStack (override van _BaseRouteComponent's virtuele getter,
    // zie daar) rapporteert aan de navigatiestack per bezoek welke van de twee routes het écht was —
    // hier kunnen dus beide een eigen, betekenisvolle vaste ouder krijgen zonder het dual-context-
    // gedrag (regel 4) nodig te hebben.
    [routePage.adminFunction]: routePage.adminPanel,
    [routePage.adminPageComponent]: routePage.adminPanel,
    [routePage.adminRole]: routePage.adminPanel,
    [routePage.page]: routePage.adminPanel,
    [routePage.theme]: routePage.adminPanel,

    // Domain en zijn kinderen
    [routePage.domain]: routePage.home,
    [routePage.category]: routePage.domain,
    [routePage.contact]: routePage.domain,
    [routePage.contactType]: routePage.domain,
    [routePage.domainUser]: routePage.domain,
    [routePage.function]: routePage.domain,
    [routePage.functionMember]: routePage.domain,
    [routePage.observationType]: routePage.domain,
    [routePage.taskType]: routePage.domain,
    [routePage.project]: routePage.domain,

    // Project en zijn kinderen
    [routePage.notice]: routePage.project,
    // Taak: ouder = project; een subtaak opent vanuit de taakpagina, de navigatiestack houdt die aanroeper vast.
    [routePage.task]: routePage.project,
    [routePage.taskCreate]: routePage.project,
    [routePage.projectMember]: routePage.project,
    [routePage.projectFunction]: routePage.project,
    [routePage.projectFunctionMember]: routePage.project,

    // Create-pagina's: ouder is de entiteit waaronder het nieuwe record valt
    [routePage.domainCreate]: routePage.home,
    [routePage.categoryCreate]: routePage.domain,
    [routePage.contactCreate]: routePage.domain,
    [routePage.contactTypeCreate]: routePage.domain,
    [routePage.domainUserCreate]: routePage.domain,
    [routePage.functionCreate]: routePage.domain,
    [routePage.observationTypeCreate]: routePage.domain,
    [routePage.taskTypeCreate]: routePage.domain,
    [routePage.projectCreate]: routePage.domain,
    [routePage.messageCreate]: routePage.project,
    [routePage.noticeCreate]: routePage.project,
    [routePage.projectMemberCreate]: routePage.project,
    [routePage.activityCreate]: routePage.adminPanel,
    [routePage.claimActionCreate]: routePage.adminPanel,
    [routePage.creditTypeCreate]: routePage.adminPanel,
    [routePage.claimCreate]: routePage.adminPanel,
    [routePage.pageComponentCreate]: routePage.adminPanel,
    [routePage.pageCreate]: routePage.adminPanel,
    [routePage.roleCreate]: routePage.adminPanel,
    [routePage.themeCreate]: routePage.adminPanel,
    [routePage.userCreate]: null,
};
